import type { LocationQuery } from 'vue-router';

import { PO_TRACKER_TAB, type PoTrackerList, type PoTrackerTab } from '@/types/poTracker';

export interface TrackerUrlState {
  vendorId: string | null;
  tab: PoTrackerTab;
  po: string;
  page: number;
}

const FULL_PO_NUMBER = /^P\d+[A-Z]{2}$/;
const FIRST_PAGE = 1;

const TABS: readonly string[] = Object.values(PO_TRACKER_TAB);

const firstString = (value: LocationQuery[string] | undefined): string => {
  const first = Array.isArray(value) ? value[0] : value;
  return typeof first === 'string' ? first : '';
};

export function normalisePo(text: string): string {
  return text.trim().toUpperCase();
}

// canSeeUpdated: only a planner has the Updated tab; a shared link to it opens All for anyone else.
export function parseTrackerQuery(q: LocationQuery, canSeeUpdated: boolean): TrackerUrlState {
  const raw = firstString(q.tab);
  const tab = raw === PO_TRACKER_TAB.UPDATED && !canSeeUpdated ? PO_TRACKER_TAB.ALL : raw;
  const page = Number.parseInt(firstString(q.page), 10);
  return {
    vendorId: firstString(q.vendor) || null,
    tab: TABS.includes(tab) ? (tab as PoTrackerTab) : PO_TRACKER_TAB.ALL,
    po: normalisePo(firstString(q.po)),
    page: Number.isFinite(page) && page >= FIRST_PAGE ? page : FIRST_PAGE,
  };
}

export function toTrackerQuery(s: TrackerUrlState): Record<string, string> {
  const query: Record<string, string> = {};
  if (s.vendorId) query.vendor = s.vendorId;
  if (s.tab !== PO_TRACKER_TAB.ALL) query.tab = s.tab;
  if (s.po) query.po = s.po;
  if (s.page !== FIRST_PAGE) query.page = String(s.page);
  return query;
}

export type VendorDecision = { kind: 'use'; id: string } | { kind: 'forbidden' } | { kind: 'none' };

// A link to a vendor outside the caller's scope is a 403, never a silent switch to another vendor.
export function vendorDecision(requested: string | null, allowed: { id: string }[]): VendorDecision {
  if (requested) return allowed.some((v) => v.id === requested) ? { kind: 'use', id: requested } : { kind: 'forbidden' };
  const first = allowed[0];
  return first ? { kind: 'use', id: first.id } : { kind: 'none' };
}

export function isFullPoNumber(po: string): boolean {
  return FULL_PO_NUMBER.test(po);
}

// listPo is the search the list answers, null while the previous search's rows are still on screen.
export function focusState(po: string, list: PoTrackerList | null, listPo: string | null): 'none' | 'single' | 'not_found' {
  if (!po || !list || listPo !== po) return 'none';
  if (list.rows.length === 1 && list.rows[0].poNumber === po) return 'single';
  if (list.rows.length === 0 && FULL_PO_NUMBER.test(po)) return 'not_found';
  return 'none';
}

// The vendor directory decides nothing while a fetch runs: a Retry clears the error before its
// request returns, and the empty directory would read as "no vendors" or a 403.
export function vendorDirectoryState(s: { ready: boolean; loading: boolean; error: string | null }): 'waiting' | 'failed' | 'ready' {
  if (!s.ready || s.loading) return 'waiting';
  return s.error ? 'failed' : 'ready';
}
